import { InfoCard } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { HomePageRecentlyVisited, HomePageTopVisited } from '@backstage/plugin-home';
import { useCallback, useMemo } from 'react';
import { useHomeCardFooterStyles } from './HomeCardFooter';
import { DEFAULT_TOP_VISITS_WINDOW_DAYS, fetchTopVisited, toVisits } from './homeVisits';
import { useAsyncValue } from './homeUtils';

/** Items shown before "View more", and the most shown after it, on both visits cards. */
export const VISITS_SHOWN = 5;
export const VISITS_TOTAL = 8;

/** Props the home card extension passes to a custom `Renderer`. */
type CardRendererProps = {
  title?: string;
  Content: (props?: any) => JSX.Element;
  Actions?: () => JSX.Element;
  ContextProvider?: (props: any) => JSX.Element;
};

/**
 * Renders a plugin-home visits card as a full-height InfoCard with the shared bottom-right
 * "View more" footer, so Recently Visited and Top Visited line up side by side.
 */
const useVisitsCardRenderer = (subheader?: string) => {
  const footer = useHomeCardFooterStyles();
  return useCallback(
    ({ title: cardTitle, Content, Actions, ContextProvider, ...childProps }: CardRendererProps) => {
      const card = (
        <InfoCard
          title={cardTitle}
          subheader={subheader}
          divider={!!cardTitle}
          variant="fullHeight"
          actionsClassName={footer.actions}
          actions={Actions ? <Actions /> : undefined}
        >
          <Content {...childProps} />
        </InfoCard>
      );
      return ContextProvider ? <ContextProvider {...childProps}>{card}</ContextProvider> : card;
    },
    [subheader, footer.actions],
  );
};

export interface RecentlyVisitedCardProps {
  title?: string;
}

/** The current user's recently visited pages (browser-local), styled like Top Visited. */
export const RecentlyVisitedCard = ({ title = 'Recently Visited' }: RecentlyVisitedCardProps) => {
  const Renderer = useVisitsCardRenderer('Your latest pages in this browser');
  return (
    <HomePageRecentlyVisited
      title={title}
      Renderer={Renderer}
      numVisitsOpen={VISITS_SHOWN}
      numVisitsTotal={VISITS_TOTAL}
    />
  );
};

export interface TopVisitedCardProps {
  title?: string;
  /** Number of pages fetched; the first five are shown, the rest behind "View more". */
  limit?: number;
}

/**
 * "Top Visited" across all DevHub users, from the home-visits backend. Same look as the
 * Recently Visited card, with a caption for the window. Errors show an empty list.
 */
export const TopVisitedCard = ({ title = 'Top Visited', limit = VISITS_TOTAL }: TopVisitedCardProps) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const { value, error } = useAsyncValue(
    () => fetchTopVisited(discoveryApi, fetchApi, limit),
    [discoveryApi, fetchApi, limit],
  );

  const visits = useMemo(() => {
    if (value) return toVisits(value.pages);
    return error ? [] : undefined;
  }, [value, error]);

  const windowDays = value?.windowDays ?? DEFAULT_TOP_VISITS_WINDOW_DAYS;
  const Renderer = useVisitsCardRenderer(
    `Most visited across DevHub · last ${windowDays} ${windowDays === 1 ? 'day' : 'days'}`,
  );

  return (
    <HomePageTopVisited
      title={title}
      Renderer={Renderer}
      visits={visits}
      loading={!visits}
      numVisitsOpen={VISITS_SHOWN}
      numVisitsTotal={limit}
    />
  );
};